import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { Button, Tooltip } from '@/shared/components';
import { PricingAvailabilityTable, ProductImage } from '@/shared/pageComponents';
import Image from 'next/image';
import React from 'react';
import Sheet from '@/assets/images/pdp/sheet.svg';
import { useProductDetailStore } from '../store';
import { hazardSymbolMapping } from '../constants';
import { SDSButton } from './sds-button';
import Link from 'next/link';
import { CatalogNotes, ChildCatalogs } from '@/types/product-detail';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { usePdpPrevPageType } from '@/shared/tracking/use-tracking-helpers';
import { useCountry } from '@/hooks/use-country';
import { usePriceStore } from '@/store/price-store';
import { getProductPrice, getFileNameFromUrl } from '@/shared/tracking/utils';
import { pdpInteractions } from '@/shared/tracking/page-load';
import { Promotion } from './promotion';

interface ProductSpecificationsProps {
  childCatalogNumbers: string[];
  specificationSheetUrl: string;
  childCatalogData: CatalogNotes[];
  childCatalogLoading: boolean;
  childCatalogs: ChildCatalogs[];
  noteLoading: boolean;
}

export const ProductSpecifications = (props: ProductSpecificationsProps) => {
  const {
    specificationSheetUrl = '',
    childCatalogLoading,
    childCatalogData = [],
    childCatalogNumbers = [],
    childCatalogs = [],
    noteLoading,
  } = props;
  const { t, lang } = useLang(NAMESPACE.pdp);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { productDetail, setBatchAddFailMessage, setFaliPriceSkus } = useProductDetailStore();
  const { productBaseInfo, properties } = productDetail;
  const { HAZARD_SYMBOL = [], CHEMICAL_IDENTIFIERS = [] } = properties;
  const { isCN, isKR } = useCountry();

  const hasSynonym =
    isCN && CHEMICAL_IDENTIFIERS?.some((item) => ['synonym', 'synonym(s)'].includes(item?.displayName?.toLowerCase()));

  const onScrollToHazard = () => {
    document.querySelector('.hazard-symbol').scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onClickCoaButton = () => {
    trackEvent({
      key: 'eventsData',
      action: 'PDPInteraction',
      payload: {
        event_name: 'PDPInteraction',
        PDP_interaction_type: pdpInteractions['coa'],
      },
    });
    document.getElementById('pdp_documents_downloads').scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const usefulHazardSymbols = HAZARD_SYMBOL.map((item) => hazardSymbolMapping[item.value]).filter(Boolean) ?? [];

  // tracking event of addToCart
  const { trackEvent } = useEventTracking();
  const prePageType = usePdpPrevPageType();
  const { productPrice } = usePriceStore();
  const onCartAdd = async (products) => {
    const res = await window.miniCart?.addProductsToCart(products);
    setBatchAddFailMessage(res?.data?.failWithMessage);
    // addToCart event tracking
    trackEvent({
      key: 'cart',
      action: 'addToCart',
      payload: {
        products: products.map((item) => {
          const price = getProductPrice(productPrice[item.catalogNumber]) * item.quantity;
          return {
            name: productBaseInfo?.productTitle,
            familyId: productBaseInfo?.globalRootSku,
            catalogId: item.catalogNumber,
            quantity: item.quantity,
            price: price,
            uom: item.unitSize,
            placement: prePageType,
            method: 'product detail commerce-box',
          };
        }),
      },
    });
  };

  return (
    <div className="flex flex-col mt-9 product-specifications">
      <div className="flex md:flex-col sm:flex-col">
        <ProductImage
          src={productBaseInfo.productImage?.[0]}
          width={180}
          height={180}
          className="ml-8 sm:ml-0 md:ml-0 sm:mt-4 md:mt-4"
        />
        <div className="ml-[70px] sm:ml-0 md:ml-0 sm:mt-4 md:mt-4 flex-grow">
          <div>
            <div className="flex sm:flex-col">
              <div className="flex gap-x-4">
                <span>{t('catalog.number')}</span>
                <span className="text-sm font-medium">{productBaseInfo.globalRootSku}</span>
              </div>
              <div className="ml-20 sm:ml-0  sm:mt-4 flex gap-x-4">
                <span>{t('sub.branding')}</span>
                <span
                  className="text-sm font-medium product-branding"
                  dangerouslySetInnerHTML={{
                    __html: productBaseInfo?.brandImage,
                  }}
                ></span>
              </div>
            </div>
            {isCN && (
              <h1
                className="text-[26px] leading-8 font-medium mt-3"
                dangerouslySetInnerHTML={{ __html: productBaseInfo?.enProductTitle }}
              ></h1>
            )}
            {((isCN &&
              lang === 'zh' &&
              productBaseInfo?.productTitle &&
              productBaseInfo?.productTitle !== productBaseInfo?.enProductTitle) ||
              !isCN) && (
              <div className="flex items-center justify-start">
                <h1
                  className="text-[26px] leading-8 font-medium mt-3"
                  dangerouslySetInnerHTML={{ __html: productBaseInfo.productTitle }}
                ></h1>
                {/* no need translation heres */}
                {isCN && (
                  <Tooltip
                    className="flex items-center"
                    placement="top"
                    maxWidth={278}
                    title="产品描述请以英文为准，中文仅做参考。"
                  >
                    <div className="icon-16-info-bold ml-1 mt-3 cursor-pointer text-info" />
                  </Tooltip>
                )}
              </div>
            )}
            <div className="mt-3">
              <div className="flex gap-x-5 sm:flex-col">
                {productBaseInfo.synonym && !hasSynonym && (
                  <div className="flex flex-[2] gap-x-2 items-center text-sm">
                    <span className="flex gap-x-2 items-center font-medium flex-shrink-0">
                      {t('synonym(s)')}
                      {isCN && (
                        <Tooltip
                          className="flex items-center"
                          placement="top"
                          maxWidth={278}
                          title={t('synonym.tooltip')}
                        >
                          <i className="icon-16-info-bold ml-1 cursor-pointer text-info" />
                        </Tooltip>
                      )}
                    </span>
                    <span
                      className="text-[#81828C]"
                      dangerouslySetInnerHTML={{ __html: productBaseInfo.synonym }}
                    ></span>
                  </div>
                )}
                {productBaseInfo.refNumber && (
                  <div className="flex flex-1 gap-x-4 text-sm flex-shrink-0 sm:mt-4">
                    <span className="font-medium">{t('ref.number')}</span>
                    <span>{productBaseInfo.refNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 mt-6 text-sm font-normal text-gray-1100 gap-y-2 gap-x-6">
            {isKR && (
              <LabelItem
                label={commonT('cas')}
                value={productBaseInfo.cas}
                tooltipVisible={false}
                link={`/search/search-result?query=${productBaseInfo.cas}`}
              />
            )}
            {!isKR && <LabelItem label={commonT('cas')} value={productBaseInfo.cas} />}
            <LabelItem label={commonT('formula')} value={productBaseInfo.formula} />
            <LabelItem label={commonT('hazard.class')} value={productBaseInfo.hazardClass} />
            <LabelItem label={commonT('einecs')} value={productBaseInfo.einecs} />
            <LabelItem label={commonT('mdl')} value={productBaseInfo.mdl} />
            <LabelItem label={commonT('packaging.group')} value={productBaseInfo.packagingGroup} />
          </div>
        </div>
        <div className="mt-6 min-w-[260px] ml-6 sm:ml-0 md:ml-0 flex flex-col justify-end">
          <span className="text-sm font-bold">{t('documents')}</span>
          <div className="flex flex-col gap-y-4 mt-6">
            <SDSButton childCatalogNumbers={childCatalogNumbers} PDPPosition={'sds_up'} />
            <Button
              onClick={onClickCoaButton}
              type="link"
              className="flex items-center gap-x-4 p-0 text-left h-auto leading-none font-bold"
            >
              <i className="icon-16-search-mono" />
              {t('COA')}
            </Button>
            {!(productBaseInfo.localProduct && isKR) && (
              <Link
                className="flex items-center gap-x-4 p-0 text-left h-auto leading-none font-bold"
                prefetch
                href={specificationSheetUrl}
                target="_blank"
                onClick={() => {
                  // code for tracking document download
                  const fileName = getFileNameFromUrl(specificationSheetUrl, productBaseInfo?.catalogNumber);
                  trackEvent({
                    key: 'eventsData',
                    action: 'documentDownloaded',
                    payload: {
                      event_name: 'documentDownloaded',
                      download_type: 'Specification Sheet',
                      download_name: fileName,
                      download_link: specificationSheetUrl,
                    },
                  });
                }}
              >
                <Image src={Sheet} alt="" width={16} height={20} />
                {t('specification.sheet')}
              </Link>
            )}
          </div>
        </div>
      </div>
      <Promotion childCatalogs={childCatalogs} />
      <PricingAvailabilityTable
        getPriceFailSkus={(skus) => setFaliPriceSkus(skus)}
        onCartAdd={onCartAdd}
        requestBulkText={t('request.bulk.quote')}
        page="pdp"
        childCatalogs={childCatalogs}
        childCatalogDetail={childCatalogData}
        noteLoading={noteLoading}
        showLoading={childCatalogLoading}
        className="mt-8"
        bulkQuoteLink={{
          pathname: '/custom-bulk',
          query: {
            sku: productBaseInfo.catalogNumber,
            cas: productBaseInfo.cas,
            title: productBaseInfo.productTitle,
          },
        }}
        additionalFooter={
          <>
            {usefulHazardSymbols.length > 0 && (
              <div className="flex gap-x-1 items-center">
                <div className="text-gray-1100 font-bold mx-5">{t('hazard.symbol')}</div>
                {usefulHazardSymbols.map((item, index) => {
                  return (
                    <Image
                      key={index}
                      src={item}
                      width={60}
                      height={60}
                      alt=""
                      className="cursor-pointer"
                      onClick={onScrollToHazard}
                    />
                  );
                })}
              </div>
            )}
          </>
        }
      />
    </div>
  );
};

interface ILabelItem {
  label: string;
  value: string;
  tooltipVisible?: boolean;
  link?: string;
}

function LabelItem({ label, value, tooltipVisible, link }: ILabelItem) {
  return (
    <>
      {value && (
        <div className="flex gap-x-4 whitespace-nowrap items-center">
          <span className="font-bold uppercase">{label}</span>
          {tooltipVisible !== false && (
            <Tooltip
              placement="top"
              title={<span dangerouslySetInnerHTML={{ __html: value }} />}
              className="overflow-hidden text-ellipsis"
            >
              <span dangerouslySetInnerHTML={{ __html: value }} />
            </Tooltip>
          )}
          {link && tooltipVisible === false && (
            <Link href={link} target="_blank" prefetch>
              {value}
            </Link>
          )}
          {!link && tooltipVisible === false && <span dangerouslySetInnerHTML={{ __html: value }} />}
        </div>
      )}
    </>
  );
}
